import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchColumnsData } from '../../dispatchers/ColumnSelectorDispatcher';
import { calculateUpdatedLayout } from '../../well-chart/WidgetLayoutService';

interface Widget {
  key: string;
  widget: string;
  widgetProperty: {
    isDefault?: boolean;
    [key: string]: any;
  };
}

interface State {
  data: any;
  loading: boolean;
  error: string | undefined;
  widgets: Widget[];
  isChartConfigUpdated: boolean;
}

const initialState: State = {
  data: undefined,
  loading: false,
  error: undefined,
  widgets: [],
  isChartConfigUpdated: false,
};

const TestWellColumnsSlice = createSlice({
  name: 'testWellColumnsSlice',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.data = undefined;
      state.error = undefined;
      state.loading = false;
      state.widgets = [];
      state.isChartConfigUpdated = false;
    },
    updateColumnData: (state, action: PayloadAction<any>) => {
      state.data = action.payload;
    },
    updateChartConfigUpdateStatus: (state, action: PayloadAction<any>) => {
      state.isChartConfigUpdated = action.payload.isChartConfigUpdated;
    },
    updateWellTestTable: (state, action: PayloadAction<Partial<Widget>>) => {
      const updatedWidgets = state.data.widgets.map((widget: any) => {
        if (widget && widget.key === 'wellTestTable') {
          return { ...widget, ...action.payload };
        }
        return widget;
      });

      state.data.widgets = updatedWidgets;
      state.data = {
        ...state.data,
      };
    },
    updateTaskTable: (state, action: PayloadAction<Partial<Widget>>) => {
      const updatedWidgets = state.data.widgets.map((widget: any) => {
        if (widget && widget.key === 'taskDetails') {
          return { ...widget, ...action.payload };
        }
        return widget;
      });

      state.data.widgets = updatedWidgets;
      state.data = {
        ...state.data,
      };
    },
    updateChartViewOptionsState: (state, action: PayloadAction<any>) => {
      const updatedWidgets = state.data.widgets.map((widget: any) => {
        if (widget && widget.key === 'timeseriesChart') {
          return { ...widget, ...action.payload };
        }
        return widget;
      });

      state.data.widgets = updatedWidgets;
      state.data = {
        ...state.data,
      };
    },
    updateChartConfig: (state, action: PayloadAction<any>) => {
      state.data.widgets = action.payload.updatedWidget;
      state.data.widgets = calculateUpdatedLayout(state.data.widgets);
      state.data = { ...state.data }; // Ensure state immutability
    },
    updateViewType: (state, action: PayloadAction<{ viewType: string }>) => {
      const updatedWidgets = state.data.widgets.map((widget: any) => {
        if (widget.key === 'timeseriesChart' || widget.key === 'wellTestChart') {
          return {
            ...widget,
            widgetProperty: {
              ...widget.widgetProperty,
              widgetProperties: {
                ...widget.widgetProperty.widgetProperties,
                chartType: action.payload.viewType,
              },
            },
          };
        }
        return widget;
      });

      state.data.widgets = calculateUpdatedLayout(updatedWidgets);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchColumnsData.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchColumnsData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchColumnsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Something went wrong';
      });
  },
});

export const {
  setInitialState,
  updateColumnData,
  updateWellTestTable,
  updateTaskTable,
  updateChartViewOptionsState,
  updateChartConfig,
  updateViewType,
  updateChartConfigUpdateStatus,
} = TestWellColumnsSlice.actions;

export default TestWellColumnsSlice.reducer;
